import React, {useEffect, useRef, useState} from 'react';
import {AlertTriangle, CheckCircle, CreditCard, PlusCircle} from 'react-feather';

import {API_BASE_URL} from '../../config/api';

import {fetchPaymentMethods} from './BillingUtils';

// ─── Shared inline styles ──────────────────────────────────────────────────
const statusMessageStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px',
  borderRadius: '8px',
  marginTop: '16px',
  fontWeight: '500',
};
const errorStyles = {
  ...statusMessageStyles,
  backgroundColor: 'rgba(239, 68, 68, 0.1)',
  color: '#b91c1c'
};
const successStyles = {
  ...statusMessageStyles,
  backgroundColor: 'rgba(34, 197, 94, 0.1)',
  color: '#15803d'
};
const selectedCardBorderColor = 'rgba(23, 157, 166, 0.45)';
const selectedCardBackground = 'rgba(23, 157, 166, 0.05)';
const selectedCardBadgeBackground = 'rgba(23, 157, 166, 0.10)';
const selectedCardBadgeText = '#12818a';
const COUNTRY_OPTIONS = [
  {value: 'CAN', label: 'Canada'},
  {value: 'USA', label: 'United States'},
];
const initialNewCardForm = {
  cardholderName: '',
  cardNumber: '',
  expiryMonth: '',
  expiryYear: '',
  cvv: '',
  street1: '',
  city: '',
  province: '',
  postalCode: '',
  country: 'CAN',
};

// ─── Helpers ────────────────────────────────────────────────────────────────
const getCardLabel = (card) => {
  const cardType = String(card?.card_type || '').trim();
  const lastFour = card?.last_four || '----';
  const isUnknown = !cardType || cardType.toLowerCase() === 'unknown';
  return isUnknown ? `**** **** **** ${lastFour}` :
                     `${cardType} ending in ${lastFour}`;
};

const formatExpiry = (card) => {
  const rawMonth = String(card?.expiry_month || '');
  const year = String(card?.expiry_year || '');
  if (!rawMonth || !year) return '';
  return `${rawMonth.padStart(2, '0')}/${year}`;
};
const normalizeYear = (value) => value.replace(/\D/g, '').slice(0, 4);
const normalizeMonth = (value) => value.replace(/\D/g, '').slice(0, 2);
const buildBillingAddressPayload = (formState) => ({
  name: formState.cardholderName.trim(),
  street_1: formState.street1.trim(),
  city: formState.city.trim(),
  province: formState.province.trim().toUpperCase(),
  postal_code: formState.postalCode.trim(),
  country: formState.country,
});

// ─── Tax summary helpers ─────────────────────────────────────────────────────
const taxRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '2px 0'
};
// "0.09975" → "9.975%", "0.13" → "13%"
const formatRatePct = (rate) =>
    `${parseFloat((parseFloat(rate) * 100).toFixed(3))}%`;

// ─── NEW_CARD sentinel ───────────────────────────────────────────────────────
const NEW_CARD = 'NEW_CARD';

// ──────────────────────────────────────────────────────────────────────────────
const PurchaseCredits = ({onPurchaseSuccess}) => {
  const [amount, setAmount] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // ── tax preview state ────────────────────────────────────────────────────
  const [taxQuote, setTaxQuote] = useState(null);
  const [taxLoading, setTaxLoading] = useState(false);

  // ── saved-card state ─────────────────────────────────────────────────────
  const [savedCards, setSavedCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(true);
  const [selectedCardId, setSelectedCardId] =
      useState(null);  // numeric card_id or NEW_CARD

  // ── legacy customer-code state (needed for new-card flow) ─────────────────
  const [customerCode, setCustomerCode] = useState(null);
  const customerCodeRef = useRef(null);
  useEffect(() => {
    customerCodeRef.current = customerCode;
  }, [customerCode]);

  // ── Helcim.js script ready flag (only matters for NEW_CARD) ───────────────
  const [helcimScriptReady, setHelcimScriptReady] = useState(false);
  const [newCardForm, setNewCardForm] = useState(initialNewCardForm);

  // ── Fetch saved cards + customer-code on mount ────────────────────────────
  useEffect(() => {
    let isMounted = true;

    const loadCards = async () => {
      setCardsLoading(true);
      try {
        const token = localStorage.getItem('access_token');

        // Fetch saved cards
        const cards = await fetchPaymentMethods(token);

        // Also fetch customer code (may 404 – that's fine)
        try {
          const ccRes = await fetch(
              `${API_BASE_URL}/api/payment/payment-info`,
              {headers: {Authorization: `Bearer ${token}`}});
          if (ccRes.ok) {
            const ccData = await ccRes.json();
            if (isMounted) setCustomerCode(ccData?.customerCode ?? null);
          }
        } catch { /* ignore */
        }

        if (!isMounted) return;
        setSavedCards(cards);

        // Pre-select default card; fall back to first card; fall back to
        // new-card form
        if (cards.length > 0) {
          const defaultCard = cards.find(c => c.is_default) || cards[0];
          setSelectedCardId(defaultCard.card_id);
        } else {
          setSelectedCardId(NEW_CARD);
        }
      } catch (err) {
        if (isMounted) {
          // Fallback: show new-card form even if we couldn't load cards
          setSavedCards([]);
          setSelectedCardId(NEW_CARD);
        }
      } finally {
        if (isMounted) setCardsLoading(false);
      }
    };

    loadCards();
    return () => {
      isMounted = false;
    };
  }, []);

  // ── Load Helcim.js only when the "new card" option is selected ────────────
  useEffect(() => {
    if (selectedCardId !== NEW_CARD)
      return;  // not needed for saved-card flow

    if (!process.env.REACT_APP_HELCIM_JS_URL) return;

    const existing = document.querySelector(
        'script[data-helcim-context="purchase-credits"]');
    if (existing) {
      setHelcimScriptReady(typeof window.helcimProcess === 'function');
      return;
    }

    const script = document.createElement('script');
    script.src = process.env.REACT_APP_HELCIM_JS_URL;
    script.async = true;
    script.dataset.helcimContext = 'purchase-credits';
    script.onload = () =>
        setHelcimScriptReady(typeof window.helcimProcess === 'function');

    document.body.appendChild(script);

    return () => {
      if (script.parentNode === document.body) {
        document.body.removeChild(script);
      }
      window.helcimCallbackResolve = null;
      window.helcimCallbackReject = null;
    };
  }, [selectedCardId]);

  // ── Fetch a tax preview whenever amount / card / province changes ─────────
  useEffect(() => {
    const purchaseAmount = parseFloat(amount);
    if (isNaN(purchaseAmount) || purchaseAmount <= 0) {
      setTaxQuote(null);
      return;
    }

    let cancelled = false;
    setTaxLoading(true);

    const handle = setTimeout(async () => {
      try {
        const token = localStorage.getItem('access_token');
        // For a new card, the typed billing province drives the tax; for
        // saved cards the backend resolves the province itself.
        const province =
            selectedCardId === NEW_CARD && newCardForm.province ?
            newCardForm.province :
            undefined;

        const res = await fetch(
            `${API_BASE_URL}/api/payment/credits/tax-quote`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
              },
              body: JSON.stringify({
                amount: purchaseAmount,
                ...(province ? {province} : {})
              }),
            });
        if (!res.ok) throw new Error('Failed to fetch tax quote.');
        const data = await res.json();
        if (!cancelled) setTaxQuote(data);
      } catch {
        if (!cancelled) setTaxQuote(null);
      } finally {
        if (!cancelled) setTaxLoading(false);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [amount, selectedCardId, newCardForm.province]);

  // ── Purchase handler ──────────────────────────────────────────────────────
  const handlePurchase = async () => {
    setErrorMessage('');
    setSuccessMessage('');

    const purchaseAmount = parseFloat(amount);
    if (isNaN(purchaseAmount) || purchaseAmount <= 0) {
      setErrorMessage('Please enter a valid purchase amount.');
      return;
    }

    setIsProcessing(true);

    try {
      const token = localStorage.getItem('access_token');
      let eventData;

      if (selectedCardId !== NEW_CARD) {
        // ── Saved-card path: just send card_id; backend resolves the token ──
        eventData = {
          event_type: 'PURCHASE_CREDITS',
          product: {price: purchaseAmount},
          chargeable: {
            card_id: selectedCardId,
            ...(customerCodeRef.current ?
                    {customer_code: customerCodeRef.current} :
                    {}),
          },
        };
      } else {
        // ── New-card path: tokenise via Helcim.js first ───────────────────
        const form = document.getElementById('helcimForm');
        if (!form) throw new Error('Payment form not found.');
        if (typeof form.reportValidity === 'function' &&
            !form.reportValidity()) {
          return;
        }

        form.submit = function() {
          const helcimResults = document.getElementById('helcimResults');
          const response =
              helcimResults?.querySelector('input[name="response"]')?.value;
          const responseMessage =
              helcimResults?.querySelector('input[name="responseMessage"]')
                  ?.value;
          const cardToken =
              helcimResults?.querySelector('input[name="cardToken"]')
                  ?.value;
          const domCode =
              helcimResults?.querySelector('input[name="customerCode"]')
                  ?.value;
          const effectiveCustomerCode =
              domCode || customerCodeRef.current || null;

          if (response === '1' && cardToken) {
            if (!effectiveCustomerCode) {
              window.helcimCallbackReject?.(new Error(
                  'Card verified, but no customer code was returned. Please try again.'));
              return false;
            }

            if (domCode && customerCodeRef.current !== domCode) {
              setCustomerCode(domCode);
              customerCodeRef.current = domCode;
            }

            window.helcimCallbackResolve?.({
              cardToken,
              customerCode: effectiveCustomerCode,
            });
          } else {
            window.helcimCallbackReject?.(
                new Error(responseMessage || 'Card verification failed.'));
          }
          return false;
        };

        const helcimResponse = await new Promise((resolve, reject) => {
          window.helcimCallbackResolve = resolve;
          window.helcimCallbackReject = reject;
          if (typeof window.helcimProcess === 'function') {
            window.helcimProcess();
          } else {
            reject(new Error('Payment gateway is not available.'));
          }
        });

        const effectiveCode =
            helcimResponse.customerCode || customerCodeRef.current || null;
        eventData = {
          event_type: 'PURCHASE_CREDITS',
          product: {price: purchaseAmount},
          chargeable: {
            card_token: helcimResponse.cardToken,
            ...(effectiveCode ? {customer_code: effectiveCode} : {}),
            billing_address: buildBillingAddressPayload(newCardForm),
          },
        };
      }

      const response =
          await fetch(`${API_BASE_URL}/api/payment/credits/purchase`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({event_data: eventData}),
          });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(
            result.error?.message || result.detail ||
            'An unknown error occurred.');
      }

      setSuccessMessage(
          result.message || 'Credits purchased successfully!');
      if (typeof onPurchaseSuccess === 'function') {
        onPurchaseSuccess(result.new_balance);
      }
      setAmount('');
      if (selectedCardId === NEW_CARD) {
        setNewCardForm(initialNewCardForm);
        // Refresh saved-cards list so newly stored card appears next time
        const freshToken = localStorage.getItem('access_token');
        fetchPaymentMethods(freshToken)
            .then(cards => {
              setSavedCards(cards);
            })
            .catch(() => {});
      }

    } catch (error) {
      setErrorMessage(error.message || 'Failed to process payment.');
    } finally {
      window.helcimCallbackResolve = null;
      window.helcimCallbackReject = null;
      setIsProcessing(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  const purchaseButtonDisabled = isProcessing || !amount || cardsLoading ||
      (selectedCardId === NEW_CARD && !helcimScriptReady) || taxLoading ||
      (parseFloat(amount) > 0 && !taxQuote);

  return (
    <div className="purchase-credits-card">
      <h3><CreditCard size={18} /> Purchase Credits</h3>
      <p>Add funds to your account balance for usage-based features.</p>

      {/* ── Amount input (always shown) ──────────────────────────────────── */}
      <div className="form-group" style={{ marginBottom: '16px' }}>
        <label htmlFor="purchaseAmount">Amount (USD)</label>
        <input
          type="number"
          id="purchaseAmount"
          name="amount"
          value={amount}
          onChange={e => setAmount(e.target.value)}
          placeholder="e.g., 25.00"
          min="1"
          step="any"
          required
          disabled={isProcessing}
        />
      </div>

      {/* ── Card picker ──────────────────────────────────────────────────── */}
      {cardsLoading ? (
        <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '12px' }}>
          Loading payment methods…
        </p>
      ) : (
        <div className="purchase-card-picker" style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px' }}>
            Payment Method
          </label>

          {savedCards.map(card => (
            <label
              key={card.card_id}
              className={`purchase-card-option ${selectedCardId === card.card_id ? 'selected' : ''}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '10px 14px',
                marginBottom: '8px',
                border: `1px solid ${selectedCardId === card.card_id ? selectedCardBorderColor : '#d1d5db'}`,
                borderRadius: '8px',
                cursor: 'pointer',
                background: selectedCardId === card.card_id ? selectedCardBackground : '#fff',
              }}
            >
              <input
                type="radio"
                name="purchase_card"
                value={card.card_id}
                checked={selectedCardId === card.card_id}
                onChange={() => setSelectedCardId(card.card_id)}
                disabled={isProcessing}
                style={{ accentColor: 'var(--primary)' }}
              />
              <CreditCard size={16} style={{ color: '#6b7280', flexShrink: 0 }} />
              <span style={{ flex: 1 }}>
                <strong>{getCardLabel(card)}</strong>
                {formatExpiry(card) && (
                  <span style={{ marginLeft: '8px', color: '#6b7280', fontSize: '0.85rem' }}>
                    Exp {formatExpiry(card)}
                  </span>
                )}
              </span>
              {card.is_default && (
                <span style={{
                  fontSize: '0.75rem',
                  backgroundColor: selectedCardBadgeBackground,
                  color: selectedCardBadgeText,
                  borderRadius: '4px',
                  padding: '2px 6px',
                }}>
                  Default
                </span>
              )}
            </label>
          ))}

          {/* 'Use a new card' option */}
          <label
            className={`purchase-card-option ${selectedCardId === NEW_CARD ? 'selected' : ''}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '10px 14px',
              border: `1px solid ${selectedCardId === NEW_CARD ? selectedCardBorderColor : '#d1d5db'}`,
              borderRadius: '8px',
              cursor: 'pointer',
              background: selectedCardId === NEW_CARD ? selectedCardBackground : '#fff',
            }}
          >
            <input
              type="radio"
              name="purchase_card"
              value={NEW_CARD}
              checked={selectedCardId === NEW_CARD}
              onChange={() => setSelectedCardId(NEW_CARD)}
              disabled={isProcessing}
              style={{ accentColor: 'var(--primary)' }}
            />
            <PlusCircle size={16} style={{ color: '#6b7280', flexShrink: 0 }} />
            <span>Use a new card</span>
          </label>
        </div>
      )}

      {/* ── New-card Helcim form (only visible when NEW_CARD selected) ───── */}
      {selectedCardId === NEW_CARD && (
        <form id="helcimForm" name="helcimForm" onSubmit={e => e.preventDefault()}>
          <div id="helcimResults"></div>
          <input type="hidden" id="token" value={process.env.REACT_APP_HELCIM_JS_TOKEN} />
          <input type="hidden" id="customerCode" value={customerCode ?? ''} />
          <input type="hidden" id="language" value="en" />
          <input type="hidden" id="cardHolderAddress" value={newCardForm.street1} />
          <input type="hidden" id="cardHolderPostalCode" value={newCardForm.postalCode} />

          <div className="payment-method-form-grid" style={{ marginTop: '16px' }}>
            <div className="form-group">
              <label htmlFor="cardHolderName">Name on Card</label>
              <input
                type="text"
                id="cardHolderName"
                name="cardHolderName"
                value={newCardForm.cardholderName}
                onChange={e => setNewCardForm(current => ({ ...current, cardholderName: e.target.value }))}
                required
                disabled={isProcessing}
              />
            </div>

            <div className="form-group payment-method-form-full">
              <label htmlFor="cardNumber">Card Number</label>
              <input
                type="text"
                id="cardNumber"
                name="cardNumber"
                value={newCardForm.cardNumber}
                onChange={e => setNewCardForm(current => ({ ...current, cardNumber: e.target.value }))}
                required
                disabled={isProcessing}
                inputMode="numeric"
              />
            </div>

            <div className="form-group">
              <label htmlFor="cardExpiryMonth">Expiry Month</label>
              <input
                type="text"
                id="cardExpiryMonth"
                name="cardExpiryMonth"
                placeholder="MM"
                value={newCardForm.expiryMonth}
                onChange={e => setNewCardForm(current => ({ ...current, expiryMonth: normalizeMonth(e.target.value) }))}
                required
                disabled={isProcessing}
                inputMode="numeric"
              />
            </div>

            <div className="form-group">
              <label htmlFor="cardExpiryYear">Expiry Year</label>
              <input
                type="text"
                id="cardExpiryYear"
                name="cardExpiryYear"
                placeholder="YY"
                value={newCardForm.expiryYear}
                onChange={e => setNewCardForm(current => ({ ...current, expiryYear: normalizeYear(e.target.value) }))}
                required
                disabled={isProcessing}
                inputMode="numeric"
              />
            </div>

            <div className="form-group">
              <label htmlFor="cardCVV">CVV</label>
              <input
                type="text"
                id="cardCVV"
                name="cardCVV"
                value={newCardForm.cvv}
                onChange={e => setNewCardForm(current => ({ ...current, cvv: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                required
                disabled={isProcessing}
                inputMode="numeric"
              />
            </div>

            <div className="form-group payment-method-form-full">
              <label htmlFor="billingStreet1">Billing Address</label>
              <input
                id="billingStreet1"
                type="text"
                value={newCardForm.street1}
                onChange={e => setNewCardForm(current => ({ ...current, street1: e.target.value }))}
                required
                disabled={isProcessing}
              />
            </div>

            <div className="form-group">
              <label htmlFor="billingCity">City</label>
              <input
                id="billingCity"
                type="text"
                value={newCardForm.city}
                onChange={e => setNewCardForm(current => ({ ...current, city: e.target.value }))}
                required
                disabled={isProcessing}
              />
            </div>

            <div className="form-group">
              <label htmlFor="billingProvince">Province /State</label>
              <input
                id="billingProvince"
                type="text"
                value={newCardForm.province}
                onChange={e => setNewCardForm(current => ({ ...current, province: e.target.value.toUpperCase().slice(0, 2) }))}
                required
                disabled={isProcessing}
                maxLength={2}
              />
            </div>

            <div className="form-group">
              <label htmlFor="billingPostalCode">Postal /ZIP</label>
              <input
                id="billingPostalCode"
                type="text"
                value={newCardForm.postalCode}
                onChange={e => setNewCardForm(current => ({ ...current, postalCode: e.target.value }))}
                required
                disabled={isProcessing}
              />
            </div>

            <div className="form-group">
              <label htmlFor="billingCountry">Country</label>
              <select
                id="billingCountry"
                value={newCardForm.country}
                onChange={e => setNewCardForm(current => ({ ...current, country: e.target.value }))}
                disabled={isProcessing}
              >
                {COUNTRY_OPTIONS.map((country) => (
                  <option key={country.value} value={country.value}>
                    {country.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <p className="payment-method-form-note" style={{ marginTop: '12px' }}>
            This uses the same secure Helcim.js card verification flow as adding a saved payment method.
          </p>
        </form>
      )}

      {/* ── Tax / total breakdown ────────────────────────────────────────── */}
      {parseFloat(amount) > 0 && taxQuote && (
        <div
          style={{
            marginTop: '16px',
            padding: '12px 14px',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            background: '#f9fafb',
            fontSize: '0.9rem',
          }}
        >
          <div style={taxRowStyle}>
            <span>Subtotal</span>
            <span>${taxQuote.subtotal.toFixed(2)}</span>
          </div>

          {taxQuote.tax_lines.length > 0 ? (
            taxQuote.tax_lines.map((line, idx) => (
              <div key={idx} style={taxRowStyle}>
                <span>{line.label} ({formatRatePct(line.rate)})</span>
                <span>${line.amount.toFixed(2)}</span>
              </div>
            ))
          ) : (
            <div style={{ ...taxRowStyle, color: '#6b7280' }}>
              <span>Tax</span>
              <span>$0.00</span>
            </div>
          )}

          <div
            style={{
              ...taxRowStyle,
              fontWeight: 700,
              borderTop: '1px solid #e5e7eb',
              marginTop: '6px',
              paddingTop: '6px',
            }}
          >
            <span>Total to charge</span>
            <span>${taxQuote.total.toFixed(2)}</span>
          </div>

          <p style={{ margin: '8px 0 0', color: '#6b7280', fontSize: '0.8rem' }}>
            ${taxQuote.subtotal.toFixed(2)} in credits will be added to your balance.
          </p>
        </div>
      )}

      {taxLoading && !taxQuote && parseFloat(amount) > 0 && (
        <p style={{ color: '#6b7280', fontSize: '0.85rem', marginTop: '12px' }}>
          Calculating tax…
        </p>
      )}

      <button
        type="button"
        onClick={handlePurchase}
        className="submit-button"
        disabled={purchaseButtonDisabled}
        style={{ marginTop: '16px' }}
      >
        {isProcessing
          ? 'Processing…'
          : (taxLoading || (parseFloat(amount) > 0 && !taxQuote))
          ? 'Calculating…'
          : taxQuote && taxQuote.tax_total > 0
          ? `Pay $${taxQuote.total.toFixed(2)}`
          : `Purchase $${parseFloat(amount || 0).toFixed(2)} Credits`}
      </button>

      {errorMessage && (<div style={errorStyles}><AlertTriangle size={18} /><span>{errorMessage}</span></div>)}
      {successMessage && (<div style={successStyles}><CheckCircle size={18} /><span>{successMessage}</span></div>)}
    </div>
  );
};

export default PurchaseCredits;
